<template>
    <div class="event__edit_sessions">
        <component-text>{{ $t('ebs.events.edit.title.sessionsAreInstances') }}</component-text>
        <form-radio-horizontal
            v-if="user.isTokenFromNewLms()"
            :name="'event-edit-sessions-session-type'"
            :label="'Session type'"
            :required="true"
            :v-validate="{ required: true }"
            :items="[
                {
                    name: 'Face-to-face',
                    value: 'face_to_face'
                },
                {
                    name: 'Virtual',
                    value: 'virtual'
                }
            ]"
            v-model="eventOccurrenceSessionType"
        >{{ eventOccurrenceSessionType === 'virtual' ? 'You can create a bespoke link for each session or use a the same link for each session by filling in the fields below.' : '' }}</form-radio-horizontal>
        <template v-if="eventOccurrenceSessionType === 'face_to_face'">
            <form-select
                :label="$t('ebs.events.edit.labels.defaultLocation')"
                :name="'default-location'"
                :value="eventOccurrenceDefaultRoomId"
                :get-items-getter="'getRoomsMappedIdNameStatus'"
                :get-items-getter-mapped="'getRoomsMappedLabelValue'"
                :get-items-dispatch="'getRooms'"
                :get-items-dispatch-data="['active']"
                :item-name="['room', 'rooms']"
                :search-queryable="false"
                :table-labels="[
                {
                    key: 'name',
                    title: 'Name',
                    type: 'string',
                    sortable: true,
                    queryable: true
                },
                {
                    key: 'status',
                    title: 'Status',
                    type: 'string',
                    sortable: true,
                    queryable: true
                }
            ]"
                @input="eventUpdateDefaultRoomId"
                @input-table="eventUpdateDefaultRoomId"
            >
                {{ $t('ebs.events.edit.title.theDefaultLocation') }} <router-link :to="{ name: 'ebs-venue-list' }">{{ $t('ebs.events.edit.buttons.viewVenues') }}</router-link>
            </form-select>
        </template>
        <template v-else-if="eventOccurrenceSessionType === 'virtual'">
            <form-text
                :name="'event-edit-sessions-virtual-meeting-link'"
                :label="'Meeting link'"
                :v-validate="{ regex: urlRegex }"
                :data-vv-as="'meeting link'"
                v-model="eventOccurrenceMeetingLink"
            >This is the link that the user will be redirected to when the select “Join online”.</form-text>
            <form-text
                :name="'event-edit-sessions-virtual-platform'"
                :label="'Platform'"
                v-model="eventOccurrencePlatform"
            >This will be the text displayed as the location for the session details. e.g - Zoom, Skype, Teams etc.</form-text>
            <form-editor
                :name="'event-edit-sessions-virtual-details'"
                :label="'Virtual access details'"
                v-model="eventOccurrenceDetails"
            >Any further information the learner will need, such as passwords, can be placed here.\nThis will be displayed for learners who have book onto the session.</form-editor>
        </template>
        <form-checkbox
            :label="$t('ebs.events.edit.labels.thisEventHasTrainers')"
            :name="'enable-trainers'"
            v-model="eventOccurrenceEnableTrainers"
        >{{ $t('ebs.events.edit.title.trainersArePeople') }}</form-checkbox>
        <component-spacing :margin-bottom="'-2rem'"/>
        <transition name="fade" mode="out-in">
            <form-select
                v-if="eventOccurrenceEnableTrainers"
                :label="$t('ebs.events.edit.labels.defaultTrainers')"
                :multiple="true"
                :name="'default-trainers'"
                :value="eventOccurrenceTrainers"
                :overlay="true"
                :get-pagination-getter="'getTrainersPagination'"
                :get-items-getter="'getTrainersMappedIdNameEmail'"
                :get-items-getter-mapped="'getTrainersMappedLabelValue'"
                :get-items-dispatch="'getTrainers'"
                :item-name="['trainer', 'trainers']"
                :search-queryable="false"
                :table-labels="[
                    {
                        key: 'name',
                        title: 'Name',
                        type: 'string',
                        sortable: true,
                        queryable: true
                    },
                    {
                        key: 'email',
                        title: 'Email',
                        type: 'string',
                        sortable: true,
                        queryable: true
                    }
                ]"
                @input="eventUpdateTrainers"
                @input-table="eventUpdateTrainers"
            >
                {{ $t('ebs.events.edit.title.titleDefaultTrainers') }} <router-link :to="{ name: 'ebs-trainer-list' }">{{ $t('ebs.events.edit.buttons.viewTrainers') }}</router-link>
            </form-select>
        </transition>
        <form-submit
            :submit-button-animate-loading="!eventHasOccurrences"
            @reset="eventReset"
            @submit="beforeEventSubmit"
        />
        <pop-notify
            ref="popnotifynotaffectsession"
            :class="'pop__notify--notaffectsession'"
            :title-text="'Save changes?'"
            :content-text="'Your changes will not affect the existing sessions of this event.'"
            :cancel-button-text="$t('ebs.events.edit.notify.buttonClose')"
            :submit-button-text="$t('ebs.events.edit.notify.buttonConfirm')"
            :prevent-close="true"
            @emit-submit="eventSubmit"
        />
    </div>
</template>

<script>
import FormSelect from '@/components/default/forms/FormSelect'
import FormSubmit from '@/components/default/forms/FormSubmit'
import FormCheckbox from '@/components/default/forms/FormCheckbox'
import ComponentSpacing from '@/components/default/shared/ComponentSpacing'
import PopNotify from '@/components/default/shared/PopNotify'
import { watchContentChangeEmit } from '@/helpers/mixins'
import ComponentText from '@/components/default/shared/ComponentText'
import FormRadioHorizontal from '@/components/default/forms/FormRadioHorizontal'
import FormText from '@/components/default/forms/FormText'
import FormEditor from '@/components/default/forms/FormEditor'
import { urlRegex } from '@/variables'
import { User } from '@/User'

export default {
  name: 'event-edit-sessions',
  components: {
    FormEditor,
    FormText,
    FormRadioHorizontal,
    ComponentText,
    PopNotify,
    ComponentSpacing,
    FormCheckbox,
    FormSubmit,
    FormSelect
  },
  data () {
    return {
      urlRegex,
      user: new User()
    }
  },
  mixins: [watchContentChangeEmit],
  computed: {
    eventOccurrenceSessionType: {
      get () {
        return this.$store.getters['getEventOccurrenceSessionType']
      },
      set (value) {
        this.$store.dispatch('setEventOccurrenceSessionType', value)
        this.$store.dispatch('setEventBookingLimitType', value === 'face_to_face' ? 'roomBased' : 'unlimited')
        this.eventOccurrenceMeetingLink = ''
        this.eventOccurrencePlatform = ''
        this.eventOccurrenceDetails = ''
        this.eventUpdateDefaultRoomId('')
      }
    },
    eventOccurrenceMeetingLink: {
      get () {
        return this.$store.getters['getEventOccurrenceMeetingLink']
      },
      set (value) {
        this.$store.dispatch('setEventOccurrenceMeetingLink', value)
      }
    },
    eventOccurrencePlatform: {
      get () {
        return this.$store.getters['getEventOccurrencePlatform']
      },
      set (value) {
        this.$store.dispatch('setEventOccurrencePlatform', value)
      }
    },
    eventOccurrenceDetails: {
      get () {
        return this.$store.getters['getEventOccurrenceDetails']
      },
      set (value) {
        this.$store.dispatch('setEventOccurrenceDetails', value)
      }
    },
    eventOccurrenceDefaultRoomId: {
      get () {
        return this.$store.getters['getEventOccurrenceDefaultRoomId']
      }
    },
    eventOccurrenceEnableTrainers: {
      get () {
        return this.$store.getters['getEventOccurrenceEnableTrainers']
      },
      set (value) {
        this.$store.dispatch('setEventOccurrenceEnableTrainers', value)
      }
    },
    eventOccurrenceTrainers: {
      get () {
        return this.$store.getters['getEventOccurrenceTrainers']
      }
    },
    watchContent () {
      return this.eventOccurrenceDefaultRoomId + this.eventOccurrenceEnableTrainers + this.eventOccurrenceTrainers
    },
    eventAllowEdit: function () {
      return this.$store.getters.getEventAllowEdit
    },
    eventAllowEditStatus: function () {
      return this.$store.getters.getEventAllowEditStatus
    },
    eventHasOccurrences () {
      return this.$store.getters.getEventHasOccurrences
    },
    eventCustomRequestLoaded () {
      return this.$store.getters.getEventCustomRequestLoaded
    }
  },
  methods: {
    validateBeforeRouteLeave: function (to) {
      if (to.name.includes('ebs-event-edit') || to.name.includes('ebs-event-add')) {
        this.$validator.validateAll().then((result) => {
          if (result) {
            this.$parent.validated = true
            this.$router.push({ path: to.path })
          } else {
            this.$refs.popnotifyvalidation.openNotify()
          }
        })
      } else {
        this.$parent.validated = true
        this.$router.push({ path: to.path })
      }
    },
    allowRedirectBeforeRouteLeave: function () {
      this.$eventBus.$emit('event-submit')
    },
    beforeEventSubmit () {
      if (this.eventHasOccurrences) {
        this.openNotifyNotAffectSessions()
      } else {
        this.eventSubmit()
      }
    },
    openNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.openNotify()
    },
    closeNotifyNotAffectSessions () {
      this.$refs.popnotifynotaffectsession.closeNotify()
    },
    checkEventStatus (value) {
      if (value === 'active' && this.eventHasOccurrencesDraft) {
        this.$refs.popnotifyhasdraftsessions.openNotify()
      }
    },
    //
    eventUpdateDefaultRoomId: function (data) {
      this.$store.dispatch('setEventOccurrenceDefaultRoomId', data)
    },
    eventUpdateTrainers: function (data) {
      this.$store.dispatch('setEventOccurrenceTrainers', data)
    },
    //
    eventReset: function () {
      this.$store.dispatch('resetEvent')
    },
    eventSubmit: function () {
      if (this.$route.name.includes('ebs-event-add')) {
        this.$store.dispatch('createEvent')
      } else if (this.$route.name.includes('ebs-event-edit')) {
        this.$store.dispatch('updateEvent')
      }
      this.allowRedirectBeforeRouteLeave()
    }
  },
  watch: {
    eventCustomRequestLoaded (value) {
      if (value) {
        this.closeNotifyNotAffectSessions()
      }
    }
  }
}
</script>
